import { useMemo } from "react";
import type { TaskAggregateGroupBy, TaskAggregateRowDto } from "@gw/shared-types";
import { SortHead, type SortState } from "@/features/requestList/components/SortHead";
import { Sparkline } from "./Sparkline";
import { GROUP_LABEL_BY_KEY } from "./GroupByChips";

interface Props {
  rows: TaskAggregateRowDto[];
  groupBy: TaskAggregateGroupBy[];
  sort: SortState;
  onSortChange: (s: SortState) => void;
  onRowClick?: (row: TaskAggregateRowDto) => void;
  unit: "bd" | "cd";
}

// The active unit's stats for one row — the server sends both bd and cd.
function pick(row: TaskAggregateRowDto, unit: "bd" | "cd") {
  return unit === "cd"
    ? { median: row.medianCd, mean: row.meanCd, p75: row.p75Cd, p95: row.p95Cd, distribution: row.distributionCd }
    : { median: row.medianBd, mean: row.meanBd, p75: row.p75Bd, p95: row.p95Bd, distribution: row.distribution };
}

function pluck(row: TaskAggregateRowDto, key: string, unit: "bd" | "cd"): number {
  const m = pick(row, unit);
  switch (key) {
    case "n":
      return row.sampleSize;
    case "median":
      return m.median;
    case "average":
      return m.mean;
    case "p75":
      return m.p75;
    case "p95":
      return m.p95;
    case "avgActions":
      return row.avgActionCount;
    case "avgActors":
      return row.avgActorCount;
    case "reopenRate":
      return row.reopenRate;
    default:
      return 0;
  }
}

export function AggregateTable({ rows, groupBy, sort, onSortChange, onRowClick, unit }: Props) {
  const sorted = useMemo(() => {
    const mult = sort.dir === "asc" ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = pluck(a, sort.key, unit);
      const bv = pluck(b, sort.key, unit);
      if (av < bv) return -1 * mult;
      if (av > bv) return 1 * mult;
      return 0;
    });
    return copy;
  }, [rows, sort, unit]);

  // Global P95 max so sparklines are visually comparable across groups (in the active unit).
  const globalMaxP95 = useMemo(
    () => Math.max(1, ...rows.map((r) => (unit === "cd" ? r.p95Cd : r.p95Bd))),
    [rows, unit],
  );

  if (rows.length === 0) {
    return (
      <div className="empty">
        <h4>No groups meet the sample threshold</h4>
        <p>Lower the minimum sample size or broaden the date range.</p>
      </div>
    );
  }

  return (
    <div className="tbl-wrap" style={{ maxHeight: "calc(100vh - 380px)" }}>
      <table className="tbl">
        <thead>
          <tr>
            {groupBy.map((k) => (
              <th key={k}>{GROUP_LABEL_BY_KEY[k]}</th>
            ))}
            <SortHead label="n" sortKey="n" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="Median" sortKey="median" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="Average" sortKey="average" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="P75" sortKey="p75" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="P95" sortKey="p95" sort={sort} setSort={onSortChange} numeric />
            <SortHead
              label="Actions"
              sortKey="avgActions"
              sort={sort}
              setSort={onSortChange}
              numeric
            />
            <SortHead label="Actors" sortKey="avgActors" sort={sort} setSort={onSortChange} numeric />
            <SortHead
              label="Reopen %"
              sortKey="reopenRate"
              sort={sort}
              setSort={onSortChange}
              numeric
            />
            <th>Distribution</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const rowKey = groupBy.map((k) => row.groupValues[k] ?? "").join("§");
            const m = pick(row, unit);
            const p95Severe = m.p95 > m.median * 4;
            return (
              <tr key={rowKey} onClick={() => onRowClick?.(row)}>
                {groupBy.map((k) => (
                  <td key={k}>
                    <span className="cell-primary">{row.groupValues[k] ?? "—"}</span>
                  </td>
                ))}
                <td className="numeric">{row.sampleSize}</td>
                <td className="numeric">
                  <strong
                    style={{
                      color:
                        m.median > 6
                          ? "var(--gw-error-600)"
                          : m.median > 3
                            ? "var(--gw-warning-700)"
                            : "inherit",
                    }}
                  >
                    {m.median.toFixed(2)}
                  </strong>
                </td>
                <td className="numeric">{m.mean.toFixed(2)}</td>
                <td className="numeric">{m.p75.toFixed(2)}</td>
                <td
                  className="numeric"
                  style={{
                    color: p95Severe ? "var(--gw-error-600)" : "inherit",
                    fontWeight: p95Severe ? 600 : 400,
                  }}
                >
                  {m.p95.toFixed(2)}
                </td>
                <td className="numeric">{row.avgActionCount.toFixed(1)}</td>
                <td className="numeric">{row.avgActorCount.toFixed(1)}</td>
                <td className="numeric">{(row.reopenRate * 100).toFixed(0)}%</td>
                <td style={{ width: 130 }}>
                  <Sparkline values={m.distribution} max={globalMaxP95} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
